import React from 'react'
import './profile.css'
import { getToken } from '../../services/LocalStorageService';
import { useGetLoggedUserQuery } from '../../services/userCRUDApi';
import {
  House,
  Signpost,
  LocationCity,
  CalendarToday,
  BadgeOutlined,
  LocationSearching,
  MailOutline,
  PhoneAndroid,
  Edit,
  Save,
} from "@mui/icons-material";
import user_image from "../profile/UserAfatar.jpg";
import { Alert, CircularProgress, Avatar, Typography, Box, Paper, Divider, Button } from "@mui/material";
import { useUpdateUserMutation } from "../../services/userCRUDApi";
import { useState, useEffect } from "react";

function Profile() {
  const { access_token } = getToken()
  const responseData = useGetLoggedUserQuery(access_token)
  const [data, setData] = useState({
    id: "",
    first_Name: "",
    last_Name: "",
    phone_Number: "",
    data_Joind: "",
    email: "",
    last_login: "",
    house_Number: "",
    street_Number: "",
    city: "",
    area: "",
  });
  const [success, setSuccess] = useState(false)
  const [server_error, setServerError] = useState({});
  const [updateUser, { isLoading }] = useUpdateUserMutation();
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    if (responseData.isSuccess) {
      setData({
        id: responseData.data.id,
        first_Name: responseData.data.first_Name,
        last_Name: responseData.data.last_Name,
        phone_Number: responseData.data.phone_Number,
        data_Joind: responseData.data.data_Joind,
        email: responseData.data.email,
        last_login: responseData.data.last_login,
        house_Number: responseData.data.address?.house_Number || "",
        street_Number: responseData.data.address?.street_Number || "",
        city: responseData.data.address?.city || "",
        area: responseData.data.address?.area || "",
      })
    }
  }, [responseData])

  const handleChange = event => {
    const { name, value } = event.target;
    setData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const actualData = {
      id: data.id,
      first_Name: data.first_Name,
      last_Name: data.last_Name,
      phone_Number: data.phone_Number,
      address_Id: responseData.data.address?.address_Id || "",
      address: {
        house_Number: data.house_Number,
        street_Number: data.street_Number,
        city: data.city,
        area: data.area,
      }
    };

    const res = await updateUser(actualData);
    if (res.error) {
      setServerError(res.error.data.errors || {});
    }
    if (res.data) {
      setSuccess(true);
      setServerError({});
      setEditMode(false);
      setTimeout(() => setSuccess(false), 3000);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  return (
    <Box className="profile-container">
      <Typography variant="h4" className="profile-title">
        User Profile
      </Typography>
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>Profile updated successfully!</Alert>
      )}

      <Box className="profile-content">
        {/* Profile Overview Card */}
        <Paper elevation={3} className="profile-card">
          <Box className="profile-header">
            <Avatar 
              src={user_image} 
              alt="User" 
              sx={{ width: 100, height: 100 }} 
              className="profile-avatar"
            />
            <Box className="profile-info">
              <Typography variant="h5" className="profile-name">
                {data.first_Name} {data.last_Name}
              </Typography>
              <Typography variant="subtitle1" className="profile-email">
                {data.email}
              </Typography>
              <Typography variant="body2" className="profile-id">
                User ID: {data.id}
              </Typography>
            </Box>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={editMode ? <Save /> : <Edit />}
              onClick={() => setEditMode(!editMode)}
              className="edit-button"
            >
              {editMode ? 'Save' : 'Edit'}
            </Button>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box className="profile-details">
            <Box className="profile-section">
              <Typography variant="h6" className="section-title">
                Account Information
              </Typography>
              <Box className="detail-item">
                <CalendarToday className="detail-icon" />
                <Box>
                  <Typography variant="body2" className="detail-label">
                    Account Created
                  </Typography>
                  <Typography variant="body1" className="detail-value">
                    {formatDate(data.data_Joind)}
                  </Typography>
                </Box>
              </Box>
              <Box className="detail-item">
                <CalendarToday className="detail-icon" />
                <Box>
                  <Typography variant="body2" className="detail-label">
                    Last Login
                  </Typography>
                  <Typography variant="body1" className="detail-value">
                    {formatDate(data.last_login)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box className="profile-section">
              <Typography variant="h6" className="section-title">
                Contact Information
              </Typography>
              <Box className="detail-item">
                <PhoneAndroid className="detail-icon" />
                <Box>
                  <Typography variant="body2" className="detail-label">
                    Phone Number
                  </Typography>
                  {editMode ? (
                    <input
                      type="text"
                      name="phone_Number"
                      value={data.phone_Number || ""}
                      onChange={handleChange}
                      className="edit-input"
                    />
                  ) : (
                    <Typography variant="body1" className="detail-value">
                      {data.phone_Number || "N/A"}
                    </Typography>
                  )}
                  {server_error.phone_Number && (
                    <Typography color="error" sx={{ fontSize: 14 }}>
                      {server_error.phone_Number[0]}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box className="detail-item">
                <MailOutline className="detail-icon" />
                <Box>
                  <Typography variant="body2" className="detail-label">
                    Email
                  </Typography>
                  <Typography variant="body1" className="detail-value">
                    {data.email}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box className="profile-section">
              <Typography variant="h6" className="section-title">
                Address
              </Typography>
              <Box className="detail-item">
                <House className="detail-icon" />
                <Box>
                  <Typography variant="body2" className="detail-label">
                    House Number
                  </Typography>
                  {editMode ? (
                    <input
                      type="text"
                      name="house_Number"
                      value={data.house_Number || ""}
                      onChange={handleChange}
                      className="edit-input"
                    />
                  ) : (
                    <Typography variant="body1" className="detail-value">
                      {data.house_Number || "N/A"}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box className="detail-item">
                <Signpost className="detail-icon" />
                <Box>
                  <Typography variant="body2" className="detail-label">
                    Street Number
                  </Typography>
                  {editMode ? (
                    <input
                      type="text"
                      name="street_Number"
                      value={data.street_Number || ""}
                      onChange={handleChange}
                      className="edit-input"
                    />
                  ) : (
                    <Typography variant="body1" className="detail-value">
                      {data.street_Number || "N/A"}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box className="detail-item">
                <LocationSearching className="detail-icon" />
                <Box>
                  <Typography variant="body2" className="detail-label">
                    Area
                  </Typography>
                  {editMode ? (
                    <input
                      type="text"
                      name="area"
                      value={data.area || ""}
                      onChange={handleChange}
                      className="edit-input"
                    />
                  ) : (
                    <Typography variant="body1" className="detail-value">
                      {data.area || "N/A"}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box className="detail-item">
                <LocationCity className="detail-icon" />
                <Box>
                  <Typography variant="body2" className="detail-label">
                    City
                  </Typography>
                  {editMode ? (
                    <select
                      name="city"
                      value={data.city || ""}
                      onChange={handleChange}
                      className="edit-select"
                    >
                      <option value="">Select City</option>
                      <option value="Coventry">Coventry</option>

                    </select>
                  ) : (
                    <Typography variant="body1" className="detail-value">
                      {data.city || "N/A"}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

          {editMode && (
            <Box className="profile-actions">
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} /> : <Save />}
                className="save-button"
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setEditMode(false)}
                className="cancel-button"
              >
                Cancel
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
}

export default Profile;